/**
 * Explore drawer loading skeleton (display-only).
 *
 * While the casino insight data is in flight the drawer's list area shows placeholder
 * EGM cards. Each one is a small three-reel slot machine (slotReels.ts) beside gray
 * placeholder text lines that pulse between 50% and 20% opacity. When the data lands
 * the top card hits check-check-check, a gold halo sweeps down and the real cards come
 * in (the "finale"); with no finale they simply cross-fade as before. This file is the
 * pure part: constants + the "should it show" decision + the timing the component and
 * the CSS share. It never touches the insight data path, sorting, API params, caches
 * or response shapes.
 *
 * CSS lives in src/styles/_modules/_m-explore-skeleton.scss and mirrors these
 * numbers (guarded by exploreSkeleton.contract.test.ts).
 */
import type { PlayingReelFinale } from './slotReels';

/** Enough placeholder cards to fill the tallest drawer snap (card + margin = 96px). */
export const SKELETON_CARD_COUNT = 8;
/** Real EGM card geometry (theme-v2 .slot-table-row): min-height 80px, 8px margin, 18px radius. */
export const SKELETON_CARD_HEIGHT_PX = 80;
export const SKELETON_CARD_MARGIN_PX = 8;
export const SKELETON_CARD_RADIUS_PX = 18;
/** Pulse range (opacity) and loop time of the placeholder text lines. */
export const SKELETON_OPACITY_HIGH = 0.5;
export const SKELETON_OPACITY_LOW = 0.2;
export const SKELETON_PULSE_MS = 1400;
/** prefers-reduced-motion: static opacity, no pulse. */
export const SKELETON_REDUCED_MOTION_OPACITY = 0.35;
/**
 * Loads that finish faster than this never show the skeleton at all (no flash
 * when data is cached / ready).
 */
export const SKELETON_APPEAR_DELAY_MS = 120;
/** Cross-fade: skeleton fades out over this long while the real cards fade in. */
export const SKELETON_LEAVE_MS = 240;
/** The real rows keep their fade-in class this long (fade + 8-row stagger + margin). */
export const SKELETON_REVEAL_MS = 800;

/**
 * True while the drawer's list area should show placeholder cards.
 *
 *  - `showDrawer`: the drawer is up (the skeleton only ever lives inside it).
 *  - `isListReportLoading` / `dataLoading`: the existing loading flags in MapStats.
 *  - `awaitingFirstData`: map casino whose first EGM load has not primed Explore yet.
 *    Without it the drawer would flash its empty-state notice between "floor map
 *    ready" and "first request started".
 */
export function shouldShowExploreSkeleton(input: {
  showDrawer: boolean;
  isListReportLoading: boolean;
  dataLoading: boolean;
  awaitingFirstData: boolean;
}): boolean {
  if (!input.showDrawer) return false;
  return input.isListReportLoading || input.dataLoading || input.awaitingFirstData;
}

export type SkeletonPhase = {
  /** Skeleton is in the DOM (loading, or still fading out). */
  mounted: boolean;
  /**
   * Skeleton is faded in (false while waiting out the appear delay and while fading
   * out). Stays true through the reel finale until the halo is most of the way down,
   * which keeps the Insight + Timeframe sections tucked away until then.
   */
  visible: boolean;
  /** The real cards are fading in under the leaving skeleton. */
  revealing: boolean;
  /** Reel finale playing after the data landed (null: none, today's cross-fade). */
  finale: PlayingReelFinale | null;
};

export const SKELETON_PHASE_IDLE: SkeletonPhase = {
  mounted: false,
  visible: false,
  revealing: false,
  finale: null,
};

/**
 * Classes for the drawer list (#slotList) from the skeleton phase.
 *  - `explore-list--finale`: the finale is playing and the halo has not started yet;
 *    the real rows (already rendered under the overlay) stay hidden.
 *  - `explore-list--reveal`: real rows fade in (today's cross-fade).
 *  - `explore-list--reels-<kind>`: with a finale, the rows fade in one by one as the
 *    halo reaches them instead of on the 45ms stagger.
 */
export function exploreListPhaseClass(phase: SkeletonPhase): string | undefined {
  const classes: string[] = [];
  if (phase.finale && !phase.revealing) classes.push('explore-list--finale');
  if (phase.revealing) {
    classes.push('explore-list--reveal');
    if (phase.finale) classes.push(`explore-list--reels-${phase.finale}`);
  }
  return classes.length > 0 ? classes.join(' ') : undefined;
}
